import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import {
  ComparatorBuilder,
  ElderBasicPaneLayoutComponent,
  ElderDataToolbarComponent,
  ElderPaneComponent,
  ElderPaneSubtitleComponent,
  ElderTableActivationDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
  UuidUtil,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { IdValidator } from '../../../../features/validators/id-validator'
import { PropertyKind } from '../../property'
import { DynamicPropertySpec } from '../dynamic-property-spec'
import { DynamicPropertySpecDetailComponent } from '../dynamic-property-spec-detail/dynamic-property-spec-detail.component'

@Component({
  selector: 'app-dynamic-property-spec-browser',
  templateUrl: './dynamic-property-spec-browser.component.html',
  styleUrls: ['./dynamic-property-spec-browser.component.scss'],
  imports: [
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
    ElderPaneSubtitleComponent,
    ElderTableComponent,
    ElderTableActivationDirective,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    DynamicPropertySpecDetailComponent,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class DynamicPropertySpecBrowserComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  private readonly _dynamicPropertySpecs$: BehaviorSubject<DynamicPropertySpec[]> =
    new BehaviorSubject([])

  private readonly _selectedSpec$ = new BehaviorSubject<DynamicPropertySpec>(null)

  private _creationMode: boolean

  public readonly idValidatorPattern = IdValidator.pattern

  @Input()
  public propertyKinds: PropertyKind[]

  @Output()
  public dynamicPropertySpecsChange: EventEmitter<DynamicPropertySpec[]> = new EventEmitter<
    DynamicPropertySpec[]
  >()

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {}

  /***************************************************************************
   *                                                                         *
   * Getter / Setters                                                        *
   *                                                                         *
   **************************************************************************/

  public get dynamicPropertySpecs$(): Observable<DynamicPropertySpec[]> {
    return this._dynamicPropertySpecs$.asObservable()
  }

  public get dynamicPropertySpecs(): DynamicPropertySpec[] {
    return this._dynamicPropertySpecs$.getValue()
  }

  @Input()
  public set dynamicPropertySpecs(specs: DynamicPropertySpec[]) {
    if (specs) {
      specs.sort(ComparatorBuilder.fieldSort('id'))
      this._dynamicPropertySpecs$.next(specs)
      this.autoSelectSpec()
    }
  }

  public get selectedSpec$(): Observable<DynamicPropertySpec> {
    return this._selectedSpec$.asObservable()
  }

  public get selectedSpec(): DynamicPropertySpec {
    return this._selectedSpec$.getValue()
  }

  public set selectedSpec(spec: DynamicPropertySpec) {
    this._selectedSpec$.next(spec)
  }

  public get creationMode(): boolean {
    return this._creationMode
  }

  public get deletionEnabled(): boolean {
    return !this.creationMode
  }

  /***************************************************************************
   *                                                                         *
   * Component Events                                                        *
   *                                                                         *
   **************************************************************************/

  public onSpecSelected(spec: DynamicPropertySpec) {
    if (spec) {
      this.selectedSpec = spec
    }
  }

  public onNewSpec(event: Event): void {
    const specs = [...this.dynamicPropertySpecs]
    const spec = new DynamicPropertySpec()
    spec.id = UuidUtil.strongUuid4()
    spec.name = 'Neu'
    specs.push(spec)
    this.dynamicPropertySpecs = specs
    this.selectedSpec = spec
    this.dynamicPropertySpecsChange.emit(this.dynamicPropertySpecs)
  }

  public onRemoveSpecs(toRemove: DynamicPropertySpec[]) {
    const dynamicProperties = [...this.dynamicPropertySpecs]
    toRemove.forEach((ss) => dynamicProperties.splice(dynamicProperties.indexOf(ss), 1))
    this.dynamicPropertySpecs = dynamicProperties
    this.autoSelectSpec()
    this.dynamicPropertySpecsChange.emit(this.dynamicPropertySpecs)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private autoSelectSpec(): void {
    let toSelect = null
    if (this.selectedSpec) {
      toSelect = this.dynamicPropertySpecs.find((spec) => spec.id === this.selectedSpec.id)
    } else if (this.dynamicPropertySpecs.length > 0) {
      toSelect = this.dynamicPropertySpecs[0]
    }

    this.selectedSpec = toSelect
  }
}
